import React, { ReactNode, useEffect, useRef } from 'react';
import { Button, Icon, IconNames } from '@dotcom/crown-ui';
import styles from './ecard-send-shell.module.scss';

export type EcardSendShellProps = {
  cardPreviewUrl?: string;
  previewAlt: string;
  playAnimationLabel: string;
  onPlayAnimation: () => void;
  children?: ReactNode;
};

export const EcardSendShell = ({
  cardPreviewUrl,
  previewAlt,
  playAnimationLabel,
  onPlayAnimation,
  children,
}: EcardSendShellProps) => {
  const contentRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hasPanel = Boolean(children);

  // Desktop sizes the preview to the form's height (see --split-height).
  useEffect(() => {
    const content = contentRef.current;
    const panel = panelRef.current;
    if (!content || !panel || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      content.style.setProperty('--split-height', `${panel.offsetHeight}px`);
    });
    observer.observe(panel);
    return () => {
      observer.disconnect();
      content.style.removeProperty('--split-height');
    };
  }, [hasPanel]);

  return (
    <main className={styles.shell} data-testid="ecard-send-view">
      <section ref={contentRef} className={styles.content} data-testid="ecard-send-content">
        <div className={styles['phone-preview']} data-testid="ecard-phone-preview">
          <div className={styles.notch} aria-hidden="true" />
          {cardPreviewUrl && (
            <img
              className={styles['card-preview']}
              src={cardPreviewUrl}
              alt={previewAlt}
              data-testid="ecard-preview-image"
            />
          )}
          <Button
            testId="play-animation-button"
            className={styles['play-animation-button']}
            onClick={onPlayAnimation}
            variant="secondary"
          >
            <Icon name={IconNames.PlaysmallBold} />
            {playAnimationLabel}
          </Button>
        </div>
        {children && (
          <div ref={panelRef} className={styles['send-panel']}>
            {children}
          </div>
        )}
      </section>
    </main>
  );
};
